import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
  [data-theme='dark'] & {
    background: linear-gradient(135deg, #212121 0%, #121212 100%);
  }
`;

const LoginBox = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 40px;
  width: 100%;
  max-width: 450px;
  box-shadow: var(--box-shadow);
  animation: slideUp 0.5s ease-out;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  }
`;

const Title = styled.h1`
  text-align: center;
  color: var(--primary-color);
  margin-bottom: 30px;
  font-weight: 700;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s;
  background-color: #f9f9f9;
  color: var(--text-primary);
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: white;
    box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
  
  [data-theme='dark'] & {
    background-color: #2d2d2d;
    border-color: #444;
    color: var(--text-primary);
    
    &:focus {
      background-color: #333;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.2);
    }
    
    &::placeholder {
      color: #777;
    }
  }
`;

const Button = styled.button`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
  box-shadow: 0 4px 8px rgba(67, 97, 238, 0.2);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(67, 97, 238, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.p`
  color: var(--error-color);
  text-align: center;
  margin-top: 20px;
  padding: 10px;
  border-radius: 8px;
  background-color: rgba(239, 71, 111, 0.1);
  animation: fadeIn 0.3s ease-in-out;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
  color: var(--primary-color);
  font-size: 2.5rem;
  font-weight: 700;
`;

const LogoAccent = styled.span`
  color: var(--secondary-color);
`;

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  
  // Redirect to admin dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/generate-qr');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(username, password);
      if (success) {
        navigate('/admin/dashboard');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <ThemeToggle />
      </div>
      <LoginBox>
        <Logo>
          Me<LogoAccent>Studio</LogoAccent>
        </Logo>
        <Title>Admin Login</Title>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              autoFocus
            />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </InputGroup>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
      </LoginBox>
    </LoginContainer>
  );
};

export default AdminLogin;
